// Frontend: TicketRaise.jsx - Form Component
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaPaperPlane, FaSpinner, FaArrowLeft, FaTimes, FaDownload } from 'react-icons/fa';
import API_BASE_URL from '../../components/apiconfig/api-config';
import axios from 'axios';

const TicketRaise = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('id');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: ''
  });

  // Fetch ticket data for edit mode
  useEffect(() => {
    if (ticketId) {
      setFetching(true);
      axios.get(`${API_BASE_URL}/api/tickets/${ticketId}`, {
        withCredentials: true
      })
      .then(response => {
        if (response.data && response.data.ticket) {
          const ticket = response.data.ticket;
          // Check if ticket can be edited (refer_back status and user owns it)
          if ((ticket.status === 'refer_back' || ticket.completion_status === 'refer_back')) {
            setIsEditMode(true);
            setFormData({
              title: ticket.title || '',
              description: ticket.description || '',
              priority: ticket.priority || 'medium',
              category: ticket.category || ''
            });
            
            // Parse existing documents
            if (ticket.documents) {
              try {
                const docs = typeof ticket.documents === 'string' 
                  ? JSON.parse(ticket.documents) 
                  : ticket.documents;
                setExistingDocuments(Array.isArray(docs) ? docs : []);
              } catch (e) {
                setExistingDocuments([]);
              }
            }
          } else {
            alert('This ticket cannot be edited. Only refer_back tickets can be edited.');
            navigate('/ticket-list');
          }
        }
      })
      .catch(error => {
        console.error('Error fetching ticket:', error);
        alert('Error loading ticket. Redirecting to ticket list.');
        navigate('/ticket-list');
      })
      .finally(() => {
        setFetching(false);
      });
    }
  }, [ticketId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingDocument = (index) => {
    setExistingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      alert('Please select a category');
      return;
    }
    
    setLoading(true);
    
    try {
      if (isEditMode && ticketId) {
        // Edit mode - use PUT request with FormData
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('priority', formData.priority);
        formDataToSend.append('category', formData.category);
        
        // Append new files
        selectedFiles.forEach(file => {
          formDataToSend.append('documents', file);
        });
        
        const response = await axios.put(`${API_BASE_URL}/api/ticket-raise-edit/${ticketId}`, formDataToSend, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data) {
          alert('Ticket updated successfully!');
          navigate('/ticket-list');
        }
      } else {
        // Create mode - use POST request with FormData
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('priority', formData.priority);
        formDataToSend.append('category', formData.category);
        
        // Append files
        selectedFiles.forEach(file => {
          formDataToSend.append('documents', file);
        });
        
        const response = await axios.post(`${API_BASE_URL}/api/ticket-raise`, formDataToSend, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data) {
          alert('Ticket submitted successfully!');
          navigate('/ticket-list');
        }
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      const errorMessage = error.response?.data?.error || 'Error submitting ticket. Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-0">
        <div className="bg-white p-3 sm:p-4 rounded overflow-hidden">
          <div className="flex flex-col items-center justify-center h-48">
            <div className="animate-spin h-8 w-8 sm:h-10 sm:w-10 border-4 border-[#10a5e9] border-t-transparent rounded-full mb-2" />
            <span className="text-gray-600 text-xs sm:text-sm">Loading ticket...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
          {isEditMode ? 'Edit Ticket' : 'Raise New Ticket'}
        </h1>
        <button
          onClick={() => navigate('/ticket-list')}
          className="flex items-center gap-2 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-md text-sm w-full sm:w-auto justify-center"
        >
          <FaArrowLeft /> Back to List
        </button>
      </div>

      <div className="bg-white p-3 sm:p-4 rounded overflow-hidden">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-4">
          {isEditMode ? 'Update Request' : 'Submit New Request'}
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2" htmlFor="title">
              Request Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
              required
              placeholder="Brief description of the change needed"
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2" htmlFor="description">
              Detailed Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="6"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
              required
              placeholder="Please provide as much detail as possible about the requested change..."
            ></textarea>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2" htmlFor="priority">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2" htmlFor="category">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
                required
              >
                <option value="" disabled hidden>Select Category</option>
                 <option value="change-record-in-database">Change Record in Database</option>
                  <option value="new-feature">New Feature</option>
                  <option value="new-functionality/update">New Functionality / Update</option>
                  <option value="formula-update">Formula Update</option>
                  <option value="ui-ux">User Interface</option>
                  <option value="new-module">New Module</option>
                  <option value="new-report">New Report</option>
              </select>
            </div>
          </div>

          {/* Document Upload Section */}
          <div className="mb-6">
            <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
              {isEditMode ? 'Add More Documents (Optional)' : 'Attach Documents (Optional)'}
            </label>
            <div className="space-y-3">
              <input
                type="file"
                id="documents"
                name="documents"
                multiple
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9]"
              />
              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">Selected files ({selectedFiles.length}):</p>
                  <div className="space-y-1">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-xs">
                        <span className="text-gray-700 truncate flex-1">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Existing Documents (Edit Mode Only) */}
          {isEditMode && existingDocuments.length > 0 && (
            <div className="mb-6">
              <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
                Existing Documents
              </label>
              <div className="space-y-2">
                {existingDocuments.map((doc, index) => {
                  const fileName = doc.split('/').pop() || doc;
                  // Construct URL: if doc is relative path like "tickets/file.pdf", use /uploads/tickets/file.pdf
                  const fileUrl = doc.startsWith('http') 
                    ? doc 
                    : `${API_BASE_URL}/uploads/${doc}`;
                  return (
                    <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded text-xs">
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#10a5e9] hover:underline flex items-center gap-1 flex-1 truncate"
                      >
                        <FaDownload className="text-xs" />
                        <span className="truncate">{fileName}</span>
                      </a>
                      <button
                        type="button"
                        onClick={() => removeExistingDocument(index)}
                        className="ml-2 text-red-500 hover:text-red-700"
                        title="Remove from list (file will remain in database)"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate('/ticket-list')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#10a5e9] text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center px-4 py-2 bg-[#10a5e9] text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-[#10a5e9] disabled:opacity-50 text-sm font-medium"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" /> {isEditMode ? 'Updating...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <FaPaperPlane className="mr-2" /> {isEditMode ? 'Update Request' : 'Submit Request'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketRaise;
